// @ts-nocheck
import React, { useState, useCallback } from 'react'
import { List, Card, Avatar, Tag, Typography, Space, Button, Badge, Empty, Spin, Tooltip, Divider, message, Pagination, Popconfirm } from 'antd'
import { useNavigate, useLocation } from 'react-router-dom'
import MaterialIcon from '../components/MaterialIcon'
import ComposeEmailModal from '../components/ComposeEmailModal'
import { useEmailList } from '../hooks/useEmailList'
import { useRealtimeEmails } from '../hooks/useRealtimeEmails'
import { useEmailNotifications } from '../hooks/useEmailNotifications'
import { useWebSocket } from '../hooks/useWebSocket'
import EmailSyncIndicator from '../components/EmailSyncIndicator'
import type { EmailThread, InboxCategory } from '../types'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

dayjs.extend(relativeTime)

const { Text, Title } = Typography

function getCategoryFromPath(pathname: string): InboxCategory {
  if (pathname === '/inbox/promotions') return 'promotions'
  if (pathname === '/inbox/updates') return 'updates'
  if (pathname === '/inbox/social') return 'social'
  if (pathname === '/inbox/forums') return 'forums'
  if (pathname === '/inbox/spam') return 'spam'
  return 'primary'
}

const CATEGORY_HEADERS: Record<InboxCategory, { title: string; icon: string }> = {
  primary: { title: 'Inbox', icon: 'inbox' },
  promotions: { title: 'Promotions', icon: 'local_offer' },
  updates: { title: 'Updates', icon: 'notifications' },
  social: { title: 'Social', icon: 'people' },
  forums: { title: 'Forums', icon: 'forum' },
  spam: { title: 'Spam', icon: 'report' },
}

const EmailList: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const category = getCategoryFromPath(location.pathname)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [filters, setFilters] = useState({ unreadOnly: false })
  const { threads, isLoading, markAsRead, pinThread, deleteThread, refresh, total } = useEmailList({
    ...filters,
    category,
    page,
    limit: pageSize,
  })
  const [draggingThread, setDraggingThread] = useState<string | null>(null)
  const [pinningThread, setPinningThread] = useState<string | null>(null)
  const [deletingThread, setDeletingThread] = useState<string | null>(null)
  const [showComposeModal, setShowComposeModal] = useState(false)
  
  // Real-time updates and notifications
  const { showNotification, showBatchNotification, requestPermission } = useEmailNotifications()
  
  const handleNewEmails = useCallback((newEmails: any[]) => {
    console.log('📬 New emails received:', newEmails.length)
    
    // Refresh the email list
    refresh()
    
    // Show notifications
    if (newEmails.length === 1) {
      showNotification(newEmails[0])
    } else if (newEmails.length > 1) {
      showBatchNotification(newEmails)
    }
  }, [refresh, showNotification, showBatchNotification])
  
  // WebSocket connection for instant real-time updates
  const { isConnected: wsConnected } = useWebSocket({
    onNewEmails: (data) => {
      console.log('🔔 WebSocket: New emails notification received', data)
      handleNewEmails(data.newEmails)
    }
  })
  
  // Polling as fallback - only runs if WebSocket is disconnected
  const { newEmailCount, unreadCount, isConnected, lastSyncAt, manualSync } = useRealtimeEmails(
    handleNewEmails,
    wsConnected  // Pass WebSocket status - polling disabled when connected
  )
  
  // Request notification permission on mount
  React.useEffect(() => {
    requestPermission()
  }, [requestPermission])

  // Reset to first page when switching category
  React.useEffect(() => {
    setPage(1)
  }, [category])

  // Separate pinned and unpinned threads
  const pinnedThreads = threads.filter(t => t.isPinned).sort((a, b) => 
    (new Date(b.pinnedAt || 0).getTime() - new Date(a.pinnedAt || 0).getTime())
  )
  const unpinnedThreads = threads.filter(t => !t.isPinned)

  const handleThreadClick = async (thread: EmailThread, e: React.MouseEvent) => {
    // Don't navigate if currently dragging
    if (draggingThread) {
      e.preventDefault()
      return
    }
    
    if (thread.isUnread) {
      await markAsRead(thread.id)
    }
    
    // Pass sender email to show ALL messages from this sender
    navigate(`/thread/${thread.id}?sender=${encodeURIComponent(thread.fromEmail)}`)
  }

  const handlePinToggle = async (e: React.MouseEvent, thread: EmailThread) => {
    e.stopPropagation() // Prevent navigating to thread
    
    setPinningThread(thread.id)
    const hide = message.loading(thread.isPinned ? 'Unpinning...' : 'Pinning...', 0)
    
    try {
      await pinThread(thread.id, !thread.isPinned)
      hide()
      message.success(thread.isPinned ? 'Conversation unpinned' : 'Conversation pinned!')
    } catch (error) {
      hide()
      message.error('Failed to update pin status')
      console.error('Pin error:', error)
    } finally {
      setPinningThread(null)
    }
  }

  const handleDelete = async (e: React.MouseEvent, thread: EmailThread) => {
    e.stopPropagation() // Prevent navigating to thread
    
    setDeletingThread(thread.id)
    const hide = message.loading('Moving to trash...', 0)
    
    try {
      await deleteThread(thread.id)
      hide()
      message.success('Email moved to trash')
    } catch (error) {
      hide()
      message.error('Failed to delete email')
      console.error('Delete error:', error)
    } finally {
      setDeletingThread(null)
    }
  }

  const handleDragStart = (e: React.DragEvent, threadId: string, isPinned: boolean) => {
    console.log('🎯 Drag started:', threadId)
    setDraggingThread(threadId)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('threadId', threadId)
    e.dataTransfer.setData('isPinned', isPinned.toString())
  }

  const handleDragEnd = () => {
    console.log('🎯 Drag ended')
    setDraggingThread(null)
  }

  const handleDrop = async (e: React.DragEvent, targetZone: 'pinned' | 'unpinned') => {
    e.preventDefault()
    const threadId = e.dataTransfer.getData('threadId')
    const wasPinned = e.dataTransfer.getData('isPinned') === 'true'
    
    // If dropped in different zone, toggle pin status
    if ((targetZone === 'pinned' && !wasPinned) || (targetZone === 'unpinned' && wasPinned)) {
      setPinningThread(threadId)
      const hide = message.loading(targetZone === 'pinned' ? 'Pinning...' : 'Unpinning...', 0)
      
      try {
        await pinThread(threadId, targetZone === 'pinned')
        hide()
        message.success(targetZone === 'pinned' ? 'Conversation pinned!' : 'Conversation unpinned')
      } catch (error) {
        hide()
        message.error('Failed to update pin status')
        console.error('Pin error:', error)
      } finally {
        setPinningThread(null)
      }
    }
    
    setDraggingThread(null)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const formatDate = (dateString: string) => {
    const date = dayjs(dateString)
    const now = dayjs()
    const diffDays = now.diff(date, 'day')

    if (diffDays === 0) {
      return date.format('h:mm A')
    } else if (diffDays === 1) {
      return 'Yesterday'
    } else if (diffDays < 7) {
      return date.format('ddd')
    } else {
      return date.format('MMM D')
    }
  }

  const renderEmailCard = (thread: EmailThread) => (
    <Card
      key={thread.id}
      draggable
      onDragStart={(e) => handleDragStart(e, thread.id, thread.isPinned)}
      onDragEnd={handleDragEnd}
      style={{ 
        marginBottom: '12px',
        cursor: draggingThread === thread.id ? 'grabbing' : 'grab',
        opacity: draggingThread === thread.id ? 0.5 : 1,
        background: thread.isUnread ? '#f6ffed' : 'white',
        borderLeft: thread.isUnread ? '4px solid #52c41a' : thread.isPinned ? '4px solid #1890ff' : 'none',
        transition: 'all 0.2s',
      }}
      onClick={(e) => handleThreadClick(thread, e)}
    >
      <List.Item style={{ border: 'none', padding: 0 }}>
        <List.Item.Meta
          avatar={
            thread.customer ? (
              <Avatar 
                icon={<MaterialIcon icon="person" />} 
                style={{ backgroundColor: '#1890ff' }} 
              />
            ) : (
              <Avatar style={{ backgroundColor: '#ea4335', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {thread.fromName?.charAt(0) || thread.fromEmail.charAt(0).toUpperCase()}
              </Avatar>
            )
          }
          title={
            <Space wrap>
              <Text strong={thread.isUnread}>
                {thread.customer 
                  ? `${thread.customer.firstName} ${thread.customer.lastName}`
                  : thread.fromName || thread.fromEmail
                }
              </Text>
              {thread.isPinned && (
                <Tag color="blue" icon={<MaterialIcon icon="push_pin" filled style={{ fontSize: '14px' }} />}>
                  Pinned
                </Tag>
              )}
              {thread.customer && (
                <Tag color="blue" icon={<MaterialIcon icon="person" style={{ fontSize: '14px' }} />}>
                  Customer
                </Tag>
              )}
              {thread.job && (
                <Tag color="green">Job #{thread.job.id}</Tag>
              )}
              {thread.aiClassification && (
                <Tag color="purple" icon={<MaterialIcon icon="smart_toy" style={{ fontSize: '14px' }} />}>
                  {thread.aiClassification.category.replace('_', ' ')}
                </Tag>
              )}
              {thread.aiClassification?.priority === 'urgent' && (
                <Tag color="red">URGENT</Tag>
              )}
            </Space>
          }
          description={
            <div>
              <Text strong={thread.isUnread} style={{ display: 'block' }}>
                {thread.subject}
              </Text>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {thread.fromEmail} • {thread.messageCount} message{thread.messageCount > 1 ? 's' : ''}
                {thread.conversationCount && thread.conversationCount > 1 && (
                  <> • {thread.conversationCount} conversation{thread.conversationCount > 1 ? 's' : ''}</>
                )}
                {thread.unreadCount && thread.unreadCount > 0 && (
                  <Tag color="orange" style={{ marginLeft: '8px' }}>
                    {thread.unreadCount} new
                  </Tag>
                )}
              </Text>
            </div>
          }
        />
        <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <Text type="secondary" style={{ fontSize: '12px', marginBottom: '8px' }}>
            {formatDate(thread.lastMessageAt)}
          </Text>
          <Space size={0}>
            <Tooltip title={thread.isPinned ? "Unpin conversation" : "Pin conversation"}>
              <Button
                type="text"
                size="small"
                icon={
                  <MaterialIcon 
                    icon="push_pin" 
                    filled={thread.isPinned}
                    style={{ 
                      color: thread.isPinned ? '#1890ff' : '#666',
                      fontSize: '18px'
                    }} 
                  />
                }
                onClick={(e) => handlePinToggle(e, thread)}
                loading={pinningThread === thread.id}
                disabled={pinningThread === thread.id}
                style={{ opacity: 0.7 }}
              />
            </Tooltip>
            <Popconfirm
              title="Move to trash?"
              description="This email will be moved to your Gmail trash."
              onConfirm={(e) => {
                e?.stopPropagation()
                handleDelete(e as unknown as React.MouseEvent, thread)
              }}
              onCancel={(e) => e?.stopPropagation()}
              okText="Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Tooltip title="Move to trash">
                <Button
                  type="text"
                  size="small"
                  icon={
                    <MaterialIcon 
                      icon="delete" 
                      style={{ 
                        color: '#ff4d4f',
                        fontSize: '18px'
                      }} 
                    />
                  }
                  onClick={(e) => e.stopPropagation()}
                  loading={deletingThread === thread.id}
                  disabled={deletingThread === thread.id}
                  style={{ opacity: 0.7 }}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        </div>
      </List.Item>
    </Card>
  )

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>Loading emails...</div>
      </div>
    )
  }

  return (
    <div>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '24px' 
      }}>
        <div>
        <Title level={3} style={{ margin: 0, marginBottom: '8px' }}>
          <MaterialIcon 
            icon={CATEGORY_HEADERS[category].icon} 
            style={{ marginRight: '8px', color: category === 'spam' ? '#ff4d4f' : '#ea4335', fontSize: '28px', verticalAlign: 'middle' }} 
          />
          {CATEGORY_HEADERS[category].title}
          <Badge
            count={unreadCount || threads.filter(t => t.isUnread).length}
            style={{ marginLeft: '12px' }}
          />
        </Title>
          <EmailSyncIndicator
            isConnected={isConnected}
            lastSyncAt={lastSyncAt}
            unreadCount={unreadCount}
            onSync={manualSync}
          />
        </div>
        
        <Space>
          <Button
            type="primary"
            icon={<MaterialIcon icon="edit" />}
            onClick={() => setShowComposeModal(true)}
            style={{ 
              background: '#1a73e8',
              borderColor: '#1a73e8',
            }}
          >
            Compose
          </Button>
          <Button
            type={filters.unreadOnly ? 'primary' : 'default'}
            onClick={() => setFilters({ ...filters, unreadOnly: !filters.unreadOnly })}
          >
            Unread Only
          </Button>
          <Button onClick={refresh}>Refresh</Button>
        </Space>
      </div>

      {threads.length === 0 ? (
        <Empty description="No emails found" />
      ) : (
        <>
          {/* Pinned Threads Section - Always show when dragging or when there are pinned threads */}
          {(draggingThread || pinnedThreads.length > 0) && (
            <div
              onDrop={(e) => handleDrop(e, 'pinned')}
              onDragOver={handleDragOver}
              style={{
                marginBottom: '32px',
                padding: '16px',
                background: draggingThread ? '#e6f7ff' : 'transparent',
                borderRadius: '8px',
                border: draggingThread ? '2px dashed #1890ff' : pinnedThreads.length === 0 ? '2px dashed #d9d9d9' : 'none',
                transition: 'all 0.3s',
                minHeight: draggingThread && pinnedThreads.length === 0 ? '80px' : 'auto',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: draggingThread && pinnedThreads.length === 0 ? 'center' : 'flex-start',
              }}
            >
            <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center' }}>
              <MaterialIcon 
                icon="push_pin" 
                filled 
                style={{ color: '#1890ff', marginRight: '8px', fontSize: '20px' }} 
              />
              <Text strong style={{ fontSize: '14px', color: '#1890ff' }}>
                PINNED ({pinnedThreads.length})
              </Text>
            </div>
              {pinnedThreads.length > 0 ? (
                <List
                  dataSource={pinnedThreads}
                  renderItem={renderEmailCard}
                />
              ) : (
                draggingThread && (
                  <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <Text type="secondary">Drop here to pin this conversation</Text>
                  </div>
                )
              )}
              {pinnedThreads.length > 0 && <Divider />}
            </div>
          )}

          {/* Regular Threads Section */}
          <div
            onDrop={(e) => handleDrop(e, 'unpinned')}
            onDragOver={handleDragOver}
            style={{
              padding: draggingThread ? '16px' : '0',
              background: draggingThread && pinnedThreads.length > 0 ? '#f0f0f0' : 'transparent',
              borderRadius: '8px',
              border: draggingThread && pinnedThreads.length > 0 ? '2px dashed #d9d9d9' : 'none',
              transition: 'all 0.3s',
            }}
          >
            {pinnedThreads.length > 0 && draggingThread && (
              <div style={{ marginBottom: '8px' }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Drop here to unpin
                </Text>
              </div>
            )}
            <List
              dataSource={unpinnedThreads}
              renderItem={renderEmailCard}
            />
          </div>

          {/* Pagination */}
          {threads.length > 0 && (
            <div style={{ 
              marginTop: '24px', 
              display: 'flex', 
              justifyContent: 'center',
              padding: '16px 0',
              borderTop: '1px solid #f0f0f0'
            }}>
              <Pagination
                current={page}
                pageSize={pageSize}
                total={total}
                onChange={(newPage, newPageSize) => {
                  setPage(newPage)
                  if (newPageSize !== pageSize) {
                    setPageSize(newPageSize)
                    setPage(1) // Reset to first page when changing page size
                  }
                  // Scroll to top
                  window.scrollTo({ top: 0, behavior: 'smooth' })
                }}
                showSizeChanger
                showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} emails`}
                pageSizeOptions={['10', '20', '50', '100']}
                showQuickJumper
              />
            </div>
          )}
        </>
      )}

      {/* Compose Email Modal */}
      <ComposeEmailModal
        visible={showComposeModal}
        onClose={() => setShowComposeModal(false)}
        onSuccess={() => {
          message.success('Bulk email sent successfully!')
          refresh()
        }}
      />
    </div>
  )
}

export default EmailList
